/**
 * LogCallModal — capture a phone call against an existing customer.
 *
 * Built for the CustomerDetailModal quick-action row. Pre-populates
 * customer identity from the passed `customer`, so the form is just
 * the four required fields (type + notes + optional urgency + optional
 * follow-up).
 *
 * Writes to the `call_logs` collection in the shape defined by the
 * `CallLog` type in src/types/schema.ts. status defaults to `'logged'`
 * (a separate "convert to deal" flow lives elsewhere — this modal's
 * job is just to capture the conversation).
 */

import { useEffect, useState } from 'react';
import { X, Phone, Loader2, AlertTriangle, Calendar as CalendarIcon } from 'lucide-react';
import { addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import type { Customer, CallLog } from '../types/schema';
import { getTenantIdSync } from '@/hooks/useTenantId';
import { logCustomerActivity } from '@/services/aggregate-customer.service';

interface LogCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  /** Optional callback fired after the call is written. Receives the new doc id. */
  onLogged?: (callId: string) => void;
}

type CallType = CallLog['callType'];
type Urgency = NonNullable<CallLog['urgency']>;

const CALL_TYPES: { id: CallType; label: string; accent: string }[] = [
  { id: 'sales',   label: 'Sales',   accent: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25' },
  { id: 'fault',   label: 'Fault',   accent: 'bg-rose-500/15 text-rose-300 border-rose-500/30 hover:bg-rose-500/25' },
  { id: 'enquiry', label: 'Enquiry', accent: 'bg-blue-500/15 text-blue-300 border-blue-500/30 hover:bg-blue-500/25' },
  { id: 'billing', label: 'Billing', accent: 'bg-purple-500/15 text-purple-300 border-purple-500/30 hover:bg-purple-500/25' },
];

const URGENCIES: { id: Urgency; label: string; accent: string }[] = [
  { id: 'low',    label: 'Low',    accent: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  { id: 'normal', label: 'Normal', accent: 'bg-white/10 text-white/80 border-white/15' },
  { id: 'high',   label: 'High',   accent: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  { id: 'urgent', label: 'Urgent', accent: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
];

export function LogCallModal({ isOpen, onClose, customer, onLogged }: LogCallModalProps) {
  const [callType, setCallType] = useState<CallType>('enquiry');
  const [notes, setNotes] = useState('');
  const [urgency, setUrgency] = useState<Urgency>('normal');
  const [requiresFollowUp, setRequiresFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal re-opens.
  useEffect(() => {
    if (isOpen) {
      setCallType('enquiry');
      setNotes('');
      setUrgency('normal');
      setRequiresFollowUp(false);
      setFollowUpDate('');
      setSaving(false);
      setError(null);
    }
  }, [isOpen]);

  // Esc closes (only when not in mid-save).
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, saving, onClose]);

  if (!isOpen || !customer) return null;

  const trimmedNotes = notes.trim();
  const canSubmit = !!callType && trimmedNotes.length > 0 && !saving;

  const handleSubmit = async () => {
    if (!canSubmit || !customer?.id) return;
    setError(null);
    setSaving(true);
    try {
      const loggedBy = localStorage.getItem('user_id') || '';
      const loggedByName = localStorage.getItem('logged_in_user') || 'Unknown';
      const tenantId = getTenantIdSync();

      const payload: Omit<CallLog, 'id'> & { tenantId?: string } = {
        callType,
        status: 'logged',
        customerId: customer.id,
        customerName: customer.name || '',
        customerPhone: customer.phone || undefined,
        customerEmail: customer.email || undefined,
        notes: trimmedNotes,
        urgency,
        loggedBy,
        loggedByName,
        // Existing call_logs entries use Firestore Timestamp (CallLog.timestamp
        // is typed as string only for the reader — sanitizers convert it).
        // We match the existing collection convention so reads keep working.
        timestamp: (Timestamp.now() as unknown) as string,
        tenantId,
        ...(requiresFollowUp ? { requiresFollowUp: true } : {}),
        ...(requiresFollowUp && followUpDate ? { followUpDate } : {}),
      };

      const docRef = await addDoc(collection(db, 'call_logs'), payload);

      // Customer 360 audit log — fire-and-forget. Logging failure must NOT
      // roll back the call log. Day 5.
      logCustomerActivity({
        customerId: customer.id!,
        actorId: loggedBy,
        actorName: loggedByName,
        entryType: 'contact_made',
        summary: `${callType} call logged${urgency && urgency !== 'normal' ? ` (${urgency})` : ''}`,
        sourceEntity: 'call',
        sourceId: docRef.id,
      }).catch((err) => console.error('[LogCallModal] customer audit log failed:', err));

      onLogged?.(docRef.id);
      onClose();
    } catch (err: unknown) {
      console.error('[LogCallModal] failed to log call:', err);
      setError((err as { message?: string })?.message || 'Failed to log the call. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="log-call-title"
      className="fixed inset-0 z-[250] flex items-center justify-center bg-black/60 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl border border-theme bg-surface-card shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-white/10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
              <Phone className="w-4 h-4 text-blue-300" />
            </div>
            <div className="min-w-0">
              <h2 id="log-call-title" className="text-base font-semibold text-white">
                Log call
              </h2>
              <p className="text-xs text-white/60 truncate">
                {customer.name || 'Customer'}
                {customer.phone ? ` · ${customer.phone}` : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="shrink-0 p-1 rounded text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Call type */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-semibold text-white/60 mb-1.5">
              Call type <span className="text-rose-400">*</span>
            </label>
            <div className="flex items-center gap-1.5 flex-wrap">
              {CALL_TYPES.map((t) => {
                const active = callType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setCallType(t.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active ? t.accent : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-semibold text-white/60 mb-1.5">
              Notes <span className="text-rose-400">*</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={saving}
              rows={5}
              placeholder="What did they call about? Outcome, decisions, anything for the next person to pick up…"
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/30 focus:border-brand-secondary resize-none"
            />
          </div>

          {/* Urgency */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-semibold text-white/60 mb-1.5">
              Urgency
            </label>
            <div className="flex items-center gap-1.5 flex-wrap">
              {URGENCIES.map((u) => {
                const active = urgency === u.id;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setUrgency(u.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active ? u.accent : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    {u.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Follow-up */}
          <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input
                type="checkbox"
                checked={requiresFollowUp}
                onChange={(e) => setRequiresFollowUp(e.target.checked)}
                disabled={saving}
                className="w-4 h-4 rounded border-white/20 bg-white/10 text-brand-secondary focus:ring-brand-secondary/30 focus:ring-offset-0"
              />
              <span>Needs follow-up</span>
            </label>
            {requiresFollowUp && (
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-white/40 shrink-0" />
                <input
                  type="date"
                  // Clamp to today onward — a follow-up in the past is
                  // almost certainly a typo. Uses LOCAL date components,
                  // not toISOString().split('T')[0] (which drifts in
                  // Australian timezones — see CLAUDE.md Critical Rules).
                  min={(() => {
                    const d = new Date();
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  })()}
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  disabled={saving}
                  className="flex-1 rounded border border-white/10 bg-white/5 px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-secondary/30"
                />
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/20 text-xs text-rose-300">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10 bg-white/5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg border border-white/15 text-sm text-white/85 hover:bg-white/10 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-brand-secondary hover:opacity-90 text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Log call
          </button>
        </div>
      </div>
    </div>
  );
}
